import { Injectable, Signal, WritableSignal, computed, signal, effect } from '@angular/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { ActivitiesInfo } from '../../../shared/models/common.models';
import { CommonService } from '../../../shared/services/common.service';
import { ConfigurationService } from '../../../shared/services/configuration.service';
import { LoggerService } from '../../../shared/services/logger.service';
import { Campaign } from '../../campaigns/campaigns.models';
import { ActivitiesByDateRangeParameters, Activity, ActivityCreditInfo, ActivityStationInfo, ActivityTab, ActivityTabHeader, CalendarActivity, Property, PropertyStationInfo, Recurrence } from '../calendars.models';
import { CalendarsService } from '../calendars.service';
import { property } from 'lodash';



@Injectable()

export class ActivitiesService
{
  public RecurrenceTypeNone = 0;
  public RecurrenceTypeDaily = 1;
  public RecurrenceTypeWeekly = 2;
  public RecurrenceTypeMonthly = 3;
  public RecurrenceTypeYearly = 4;

  public closeAfterSave: boolean = true;

  public activitiesInfo: ActivitiesInfo;
  public currentUserFullname: string = "";

  private getActivitiesForPropertyAndDateRangeReturnedZeroResults: boolean = false;
  private lastActivitiesParamsKey: string | null = null;
  public lastFetchError: WritableSignal<string | null> = signal(null);
  private isManualFetching: boolean = false;


  public tabSelectedIndex: WritableSignal<number> = signal(0);
  public activityId: WritableSignal<number> = signal(0);
  public stationIds: WritableSignal<number[]> = signal([]);
  public calendarActivity: WritableSignal<CalendarActivity> = signal(null);
  public activitiesParameters: WritableSignal<ActivitiesByDateRangeParameters> = signal(null);



  public queryActivitiesForPropertyAndDateRange = injectQuery(() =>
  ({
    enabled:
      this.configurationService.cbSettings().calendarServiceUrl.length > 0 &&
      this.activitiesParameters() != null &&
      !!this.activitiesParameters()?.stationIds &&
      this.activitiesParameters()?.stationIds?.length > 0 &&
      (
        (this.activityId() == null || this.activityId() === 0) ||
        this.activitiesInfo?.newActivityOnOccupiedSpot === true ||
        !!this.activitiesInfo?.campaign
      ),
    queryKey: ["Activities", this.activitiesParameters()],
    queryFn: () => this.getRelevantActivities(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: attempt => Math.min(1000 * 2 ** attempt, 5000),
    onError: (err: any) =>
    {
      const msg = err?.message ?? (typeof err === 'string' ? err : JSON.stringify(err));
      this.lastFetchError.set(msg);
      this.loggerService.error(`Failed to fetch activities: ${msg}`);
    }
  }))

  public queryActivity = injectQuery(() =>
  ({
    enabled: this.configurationService.cbSettings().calendarServiceUrl.length > 0 && this.activityId() != null && this.activityId() > 0,
    queryKey: ["Activity", this.activityId()],
    queryFn: () => this.calendarsService.getActivity(this.activityId()),
    //staleTime: this.configurationService.cbSettings().queryStaleTime,
  }))


  public activities: WritableSignal<Activity[]> = signal([]);

  public tabs: Signal<ActivityTab[]> = computed(() =>
  {
    const ats: ActivityTab[] = [];
    const items = this.activities();

    if (items.length > 0)
    {
      items.forEach(a =>
      {
        const at: ActivityTab = this.getActivityTab(a);
        if (!ats.some(t => t.content.Id === at.content.Id))
        {
          ats.push(at);
        }
      });
    }

    return ats;
  });

  constructor(
    private commonService: CommonService,
    private loggerService: LoggerService,
    private configurationService: ConfigurationService,
    private calendarsService: CalendarsService
  )
  {
    effect(async () =>
    {
      const ap = this.activitiesParameters();
      const ca = this.calendarActivity();

      const enabled =
        this.configurationService.cbSettings().calendarServiceUrl.length > 0 &&
        ap != null &&
        !!ap?.stationIds &&
        ap.stationIds.length > 0;

      if (!enabled || this.isManualFetching)
      {
        return;
      }

      const key = JSON.stringify({
        ap,
        stationIds: ap.stationIds ?? [],
        caId: ca?.Id ?? 0
      });

      if (key === this.lastActivitiesParamsKey)
      {
        return;
      }
      this.lastActivitiesParamsKey = key;

      this.isManualFetching = true;
      try
      {
        const refetchResult = await this.queryActivitiesForPropertyAndDateRange.refetch({ cancelRefetch: false });
        const result = (refetchResult?.data as Activity[]) ?? [];

        if (refetchResult?.status === 'success')
        {
          this.activities.set(result);
          this.setTabSelectedIndex();
          this.lastFetchError.set(null);

          this.getActivitiesForPropertyAndDateRangeReturnedZeroResults = result.length === 0;
          if (result.length === 0)
          {
            this.loggerService.error(`getActivitiesForPropertyAndDateRange returned zero results so had to getActivity only.  User: "${this.currentUserFullname}, parameters: ${ca?.PropertyId}|${ca?.FirstDateInCalendarRange}|${ca?.LastDateInCalendarRange}|${JSON.stringify(this.stationIds())}|${ca?.Level}`);
          }
        }
      }
      finally
      {
        this.isManualFetching = false;
      }
    }, { allowSignalWrites: true });

    effect(async () =>
    {
      const id = this.activityId();

      // avoid overlapping with other manual fetches
      if (this.isManualFetching) return;

      this.isManualFetching = true;

      try
      {
        const res = await this.queryActivity.refetch({ cancelRefetch: false });
        if (res?.status === 'success' && res.data)
        {
          const act = res.data as Activity;

          if (!id || id <= 0)
          {
            this.CreateNewActivity(this.calendarActivity(), act.Property);
          }
          else
          {
            if (this.getActivitiesForPropertyAndDateRangeReturnedZeroResults)
            {
              act.CalendarActivityId = this.calendarActivity()?.Id;
              act.ActivitiesParameters = this.activitiesParameters();
            }
            else
            {
              act.IsForCampaign = this.activitiesInfo?.IsForCampaign;
            }

            this.upsertActivity(act);
          }

          this.setTabSelectedIndex();
          this.lastFetchError.set(null);
        }
      }
      finally
      {
        this.isManualFetching = false;
      }
    }, { allowSignalWrites: true });
  }

  public addActivity(activity: Activity)
  {
    const items = this.activities();

    if (!items.some(t => t.Id === activity.Id))
    {
      this.activities.set([...items, activity]);
    }
  }

  public removeActivity(activityId: number)
  {
    const items = this.activities().filter(a => a.Id !== activityId);
    this.activities.set(items);
    this.setTabSelectedIndex();
  }

  public setTabSelectedIndex()
  {
    const items = this.activities();
    const index = items.length > 0 ? items.length - 1 : 0;
    this.tabSelectedIndex.set(index);
  }


  public async getActivitiesForPropertyAndDate()
  {
    // Avoid overlapping with the effect (or another manual call)
    if (this.isManualFetching) return;

    const ap = this.activitiesParameters();
    const ca = this.calendarActivity();

    const enabled =
      this.configurationService.cbSettings().calendarServiceUrl.length > 0 &&
      ap != null &&
      !!ap?.stationIds &&
      ap.stationIds.length > 0;

    if (!enabled) return;

    this.lastFetchError.set(null);
    this.isManualFetching = true;

    try
    {
      const refetchResult = await this.queryActivitiesForPropertyAndDateRange.refetch({ cancelRefetch: false });
      const status = refetchResult?.status;

      if (status === 'success')
      {
        const result = (refetchResult?.data as Activity[]) ?? [];
        if (result.length > 0)
        {
          this.activities.set(result);
          this.getActivitiesForPropertyAndDateRangeReturnedZeroResults = false;
          this.setTabSelectedIndex();
        }
        else
        {
          this.loggerService.error(`getActivitiesForPropertyAndDateRange returned zero results so had to getActivity only.  User: "${this.currentUserFullname}, parameters: ${ca?.PropertyId}|${ca?.FirstDateInCalendarRange}|${ca?.LastDateInCalendarRange}|${JSON.stringify(this.stationIds())}|${ca?.Level}`);
          this.getActivitiesForPropertyAndDateRangeReturnedZeroResults = true;
        }
      }
      else if (status === 'error')
      {
        const err: any = (refetchResult as any)?.error ?? this.queryActivitiesForPropertyAndDateRange.error();
        const msg = err?.message ?? (typeof err === 'string' ? err : JSON.stringify(err));
        this.lastFetchError.set(msg);
        this.loggerService.error(`Error fetching activities: ${msg}. User: "${this.currentUserFullname}", parameters: ${ca?.PropertyId}|${ca?.FirstDateInCalendarRange}|${ca?.LastDateInCalendarRange}|${JSON.stringify(this.stationIds())}|${ca?.Level}`);
        this.getActivitiesForPropertyAndDateRangeReturnedZeroResults = false;
      }
    }
    catch (e: any)
    {
      const msg = e?.message ?? (typeof e === 'string' ? e : JSON.stringify(e));
      this.lastFetchError.set(msg);
      this.loggerService.error(`Unhandled error fetching activities: ${msg}`);
      this.getActivitiesForPropertyAndDateRangeReturnedZeroResults = false;
    }
    finally
    {
      this.isManualFetching = false;
    }
  }

  public async getActivity()
  {
    if (this.activityId() > 0)
    {
      const refetch = await this.queryActivity.refetch({ cancelRefetch: false });
      const result: any = (refetch?.data as any) ?? null;

      if (result && result.Id)
      {
        if (this.getActivitiesForPropertyAndDateRangeReturnedZeroResults)
        {
          result.CalendarActivityId = this.calendarActivity()?.Id;
          result.ActivitiesParameters = this.activitiesParameters();
        }
        else
        {
          result.IsForCampaign = this.activitiesInfo?.IsForCampaign;
          this.closeAfterSave = true;
        }

        this.addActivity(result);
      }
    }
  }


  public async getRelevantActivities()
  {
    const ca = this.calendarActivity();
    const ap = this.activitiesParameters();

    let activities: Activity[] = [];

    try
    {
      const campaign: Campaign = this.activitiesInfo?.campaign;

      if (campaign)
      {
        const activity = new Activity();
        activity.CampaignId = campaign.Id;
        activity.IsForCampaign = true;
        activity.BookingId = campaign.BookingId;
        activity.ClientId = campaign.Client.Id;
        activity.ClientName = campaign.Client.Name;
        activity.ClientContact = campaign.Client.Contact;
        activity.ClientContactEmail = campaign.Client.ContactEmail;
        activity.ClientContactPhoneNo = campaign.Client.ContactPhoneNo;
        activity.ClientExecUserId = campaign.ClientExecUserId;
        activity.ClientSupportUserId = campaign.ClientSupportUserId;
        activity.BriefManagerUserId = campaign.BriefManagerUserId;
        activity.NatProjectManagerUserId = campaign.NatProjectManagerUserId;
        activity.Product = campaign.Product;

        activities.push(activity);
      }
      // Fetch from DB when there is no specific activityId OR we are adding a new activity on an occupied spot
      else if (this.activityId() == null || this.activityId() != 0 || this.activitiesInfo?.newActivityOnOccupiedSpot == true)
      {
        activities = await this.calendarsService.getActivitiesForPropertyAndDateRange(ap);

        try
        {
          if (this.activitiesInfo?.newActivityOnOccupiedSpot)
          {
            const act: Activity = await this.CreateNewActivityAsync(ca);
            if (act)
            {
              activities.push(act);
            }
          }
        }
        catch { /* ignore */ }
      }
      else
      {
        const activity: Activity = await this.CreateNewActivityAsync(ca);

        if (activity && !activities.some(t => t.Id == activity.Id))
        {
          // add activities to the array which will be cached by the query function along with DB results
          activities.push(activity);
        }
      }

      if (activities)
      {
        activities.map((activity: any) =>
        {
          if (ca != null)
          {
            activity.CalendarActivityId = ca?.Id;
          }

          if (ap != null)
          {
            activity.ActivitiesParameters = ap;
          }

          activity.isOnline = false;
        });
      }
    }
    catch (e: any)
    {
      console.log(e.message);
    }

    // return results (from database and manually added) to the query function to be cached and returned from query key
    return activities;
  }

  public async CreateNewActivityAsync(ca: CalendarActivity)
  {
    let activity: Activity = null;

    if (ca)
    {
      activity = new Activity();

      if (ca.PropertyId > 0)
      {
        activity.Property = await this.calendarsService.getProperty(ca.PropertyId);

        if (activity.Property)
        {
          activity = this.CreateNewActivity(ca, activity.Property);
        }
      }
    }

    return activity;
  }


  public CreateNewActivity(ca: CalendarActivity, property: Property)
  {
    let activity: Activity = null;

    if (ca)
    {
      activity = new Activity();

      if (property)
      {
        activity.Property = property;

        if (this.calendarsService.mechanicTypes() && this.calendarsService.mechanicTypes().length > 0)
        {
          activity.MechanicType = this.calendarsService.mechanicTypes()[0];
        }

        if (activity.Property && activity.Property.StationSpecificInfo && activity.Property.StationSpecificInfo.length > 0)
        {
          activity.Property.StationSpecificInfo.map((psi: PropertyStationInfo) =>
          {
            if (psi && psi.StationId > 0)
            {
              if (!activity.IsBlocker)
              {
                //if (!act.Stations.some((a: number) => a == psi.StationId))
                //{
                //  act.Stations.push(psi.StationId);
                //}

                const asi: ActivityStationInfo = new ActivityStationInfo();
                asi.ProjectManagerId = psi.ProjectManagerId;
                asi.AdOpsId = psi.AdOpsId;
                asi.StationId = psi.StationId;
                asi.OnAirTime = psi.OnAirTime;
                asi.IsRateCpm = psi.IsRateCpm;
                asi.Rate = psi.Rate;

                asi.ActivityCreditInfos = [];

                psi.PropertyCreditInfos.map(pci =>
                {
                  const aci: ActivityCreditInfo = new ActivityCreditInfo();

                  aci.ActivityId = activity.Id;
                  aci.DayPartId = pci.DayPartId;
                  aci.DayPartLabel = pci.DayPartLabel;
                  aci.Disabled = pci.Disabled;
                  aci.Fri = pci.Fri;
                  aci.Mon = pci.Mon;
                  aci.Sat = pci.Sat;
                  aci.StationId = psi.StationId;
                  aci.Sun = pci.Sun;
                  aci.Thu = pci.Thu;
                  aci.Tue = pci.Tue;
                  aci.Wed = pci.Wed;

                  asi.ActivityCreditInfos.push(aci);
                });

                activity.ActivityStationInfos.push(asi);
                activity.Stations.push(asi.StationId);
              }
              else
              {
                activity.Stations.push(psi.StationId);
              }
            }
          });
        }

        if (activity?.Property?.PropertyType?.IsBroadcast)
        {
          //remove station records for stations apart from the one clicked
          if (!activity.IsBlocker)
          {
            activity.ActivityStationInfos = activity.ActivityStationInfos.filter(asi => ca.StationId == asi.StationId);
          }
          activity.Stations = activity.Stations.filter(asi => ca.StationId == asi);
        }

        activity.EndDate = ca.EndDate;
        activity.Level = ca.Level;
        activity.StartDate = ca.StartDate;
        activity.Id = ca.Id;
        activity.ClientName = ca.ClientName;

        activity.isOnline = false;
        activity.ActivityHeader = `New ${activity.Property != null ? activity.Property.PropertyName : activity.PropertyName} Activity - Pending`;

        const recurrence: Recurrence = new Recurrence();
        recurrence.PatternStartDate = this.commonService.parseJsonDate(activity.StartDate);
        recurrence.PatternEndDate = this.commonService.parseJsonDate(activity.EndDate);
        recurrence.RecurrenceType = this.RecurrenceTypeNone;
        recurrence.Id = 0;

        activity.Recurrence = recurrence;
      }
    }

    return activity;
  }

  private upsertActivity(activity: Activity)
  {
    const items = this.activities();
    const idx = items.findIndex(a => a.Id === activity.Id);
    if (idx > -1)
    {
      const clone = [...items];
      clone[idx] = activity;
      this.activities.set(clone);
    }
    else
    {
      this.activities.set([...items, activity]);
    }
  }

  private getActivityTab(a: Activity)
  {
    const at: ActivityTab = new ActivityTab();
    at.header = new ActivityTabHeader();

    if (!a.ActivityHeader)
    {
      let status = "Pending";
      a.Status = "Pending";

      if (a.OnHold)
      {
        status = "On Hold";
        a.Status = "OnHold";
      }

      if (a.IsGuaranteed)
      {
        status = "First Right Refusal";
        a.Status = "IsGuaranteed";
      }

      if (a.IsUnConfirmedSold)
      {
        status = "Submitted For Delivery";
        a.Status = "IsUnConfirmedSold";
      }

      if (a.IsSold)
      {
        status = "Confirmed";
        a.Status = "IsSold";
      }

      a.ActivityHeader = `${a.ClientName} ${a.Product} - ${status}`;
    }

    at.header.headerText = a.ActivityHeader;
    at.header.headerClass = this.getClassForActivityStatus(a.Status);
    at.content = a;

    return at;
  }



  public getClassForActivityStatus(status: string)
  {
    let headerClass: any = { 'dialog-header': true, 'pending-activity': true };

    switch (status)
    {
      case "Pending":
        headerClass = { 'dialog-header': true, 'pending-activity': true };
        break;
      case "IsSold":
        headerClass = { 'dialog-header': true, 'sold-activity': true };
        break;
      case "IsGuaranteed":
        headerClass = { 'dialog-header': true, 'guaranteed-activity': true };
        break;
      case "OnHold":
        headerClass = { 'dialog-header': true, 'onhold-activity': true };
        break;
      case "IsUnConfirmedSold":
        headerClass = { 'dialog-header': true, 'unconfirmed-sold-activity': true };
        break;
      default:
        headerClass = { 'dialog-header': true, 'pending-activity': true };
        break;
    }

    return headerClass;
  }



}
